import { useMemo } from 'react';

/**
 * useAcademicInsights — generates max 3 short, intelligent insights
 * that interpret patterns in the student's data, without repeating
 * Snapshot counts or Upcoming Assessments list content.
 *
 * Insight types (priority order):
 *   A. Assessment cluster — multiple assessments on the same day
 *   B. Overdue risk — overdue items dragging your grade
 *   C. Workload spike / moderate workload — items due in next 7 days
 *  C2. 48-hour deadline pressure — multiple items due very soon
 *   D. Strong / good momentum — completion rate last week
 *   E. Calm period — light week ahead
 *
 * @param {object} health — from useAcademicHealth
 * @param {array}  assessments — from useUpcomingAssessments (sorted by date)
 * @returns {{ insights: Insight[] }}
 */
export default function useAcademicInsights(health, assessments = []) {
  return useMemo(() => {
    if (!health) return { insights: [] };

    const insights = [];

    // ── A. Assessment alerts (cluster OR upcoming) ──────────────────────
    // Group assessments by calendar day within next 14 days
    const now = new Date();
    const in14d = new Date(now.getTime() + 14 * 24 * 60 * 60 * 1000);
    const clusters = findAssessmentClusters(assessments, now, in14d);

    if (clusters.length > 0) {
      const top = clusters[0]; // biggest/soonest cluster
      const daysUntil = diffDays(now, top.date);
      const daysStr = daysUntil === 0 ? 'today'
        : daysUntil === 1 ? 'tomorrow'
        : `in ${daysUntil} days`;

      // List unique course names in this cluster
      const courseNames = [...new Set(top.items.map(i => i.courseName || i.courseCode).filter(Boolean))];
      const courseNote = courseNames.length === 1
        ? ` for ${courseNames[0]}`
        : courseNames.length > 1
          ? ` across ${courseNames.join(', ')}`
          : '';

      insights.push({
        id: 'cluster',
        icon: 'brain',
        severity: top.count >= 4 ? 'critical' : 'warning',
        title: 'Assessment cluster detected',
        description: `You have ${top.count} assessments${courseNote} on ${top.dateLabel} (${daysStr}). Start prep earlier this week.`,
        priority: 0,
      });
    }

    // If no cluster fired, check for any assessment in next 5 days
    if (clusters.length === 0 && assessments.length > 0) {
      const in5d = new Date(now.getTime() + 5 * 24 * 60 * 60 * 1000);
      const upcoming = assessments.filter(a => a.date && a.date > now && a.date <= in5d);
      if (upcoming.length > 0) {
        const next = upcoming[0]; // soonest
        const daysUntil = diffDays(now, next.date);
        const daysStr = daysUntil === 0 ? 'today'
          : daysUntil === 1 ? 'tomorrow'
          : `in ${daysUntil} days`;
        const label = (next.title || 'Assessment').length > 30
          ? (next.title || 'Assessment').slice(0, 28) + '\u2026'
          : (next.title || 'Assessment');

        const course = next.courseName || next.courseCode || '';
        const courseStr = course ? ` for ${course}` : '';

        if (upcoming.length === 1) {
          insights.push({
            id: 'upcoming',
            icon: 'calendar',
            severity: daysUntil <= 1 ? 'warning' : 'info',
            title: `${label} coming up`,
            description: `${label}${courseStr} is ${daysStr} (${next.dateLabel}). Make sure you\u2019re prepared.`,
            priority: 0,
          });
        } else {
          insights.push({
            id: 'upcoming',
            icon: 'calendar',
            severity: 'warning',
            title: `${upcoming.length} assessments this week`,
            description: `Next up: ${label}${courseStr} ${daysStr}. Plan your study sessions now.`,
            priority: 0,
          });
        }
      }
    }

    // ── B. Overdue risk ───────────────────────────────────────────────────
    const overdueCount = health.overdueCount || 0;
    if (overdueCount > 0) {
      insights.push({
        id: 'overdue',
        icon: 'warning',
        severity: overdueCount >= 3 ? 'critical' : 'warning',
        title: 'Overdue items dragging your grade',
        description: `${overdueCount} overdue. Knock out the oldest first to reset momentum.`,
        priority: 1,
      });
    }

    // ── C. Workload spike / moderate workload ─────────────────────────────
    const dueNext7d = health.dueSoon7dCount || 0;
    const dueNext48h = health.dueNext48hCount || 0;
    if (dueNext7d >= 8) {
      insights.push({
        id: 'spike',
        icon: 'calendar',
        severity: 'warning',
        title: 'Workload spike approaching',
        description: `${dueNext7d} items due in the next 7 days. Plan two focused blocks.`,
        priority: 2,
      });
    } else if (dueNext7d >= 4) {
      insights.push({
        id: 'moderate',
        icon: 'calendar',
        severity: 'info',
        title: 'Steady week ahead',
        description: `${dueNext7d} items due this week. Stay consistent and you\u2019ll be in good shape.`,
        priority: 2,
      });
    }

    // ── C2. 48-hour deadline pressure ─────────────────────────────────────
    if (dueNext48h >= 2) {
      insights.push({
        id: 'deadline48h',
        icon: 'warning',
        severity: dueNext48h >= 4 ? 'warning' : 'info',
        title: `${dueNext48h} due in the next 48 hours`,
        description: `Focus on what\u2019s closest first to keep your momentum.`,
        priority: 1.5,
      });
    }

    // ── D. Strong / good momentum ─────────────────────────────────────────
    const completed7d = health.completedLast7dCount || 0;
    if (completed7d >= 10) {
      insights.push({
        id: 'momentum',
        icon: 'trendUp',
        severity: 'good',
        title: 'Strong momentum',
        description: `${completed7d} completed last week. Keep the streak alive.`,
        priority: 3,
      });
    } else if (completed7d >= 4) {
      insights.push({
        id: 'momentum',
        icon: 'trendUp',
        severity: 'good',
        title: 'Good pace last week',
        description: `${completed7d} completed recently. A few more and you\u2019re ahead of the curve.`,
        priority: 3,
      });
    }

    // ── E. Calm period ────────────────────────────────────────────────────
    if (dueNext7d <= 2 && overdueCount === 0) {
      insights.push({
        id: 'calm',
        icon: 'check',
        severity: 'good',
        title: 'Light week ahead',
        description: dueNext7d === 0
          ? 'Nothing urgent this week. Get ahead on readings or prep.'
          : `Only ${dueNext7d} due soon. Get ahead on readings or prep.`,
        priority: 4,
      });
    }

    // ── If overdue is large AND cluster exists, overdue goes first ────────
    if (overdueCount >= 3 && clusters.length > 0) {
      // Swap priorities so overdue comes before cluster
      const overdueInsight = insights.find(i => i.id === 'overdue');
      const clusterInsight = insights.find(i => i.id === 'cluster');
      if (overdueInsight && clusterInsight) {
        overdueInsight.priority = -1;
      }
    }

    // Sort by priority and cap at 3
    insights.sort((a, b) => a.priority - b.priority);
    return { insights: insights.slice(0, 3) };
  }, [health, assessments]);
}

// ── Helpers ─────────────────────────────────────────────────────────────────

function findAssessmentClusters(assessments, now, cutoff) {
  // `assessments` is now an array of AssessmentGroups (each = 1 logical assessment).
  // Each group counts as 1 assessment for cluster detection.
  const byDay = new Map();

  for (const a of assessments) {
    if (!a.date || a.date < now || a.date > cutoff) continue;
    const key = a.date.toISOString().slice(0, 10);
    if (!byDay.has(key)) {
      byDay.set(key, { date: a.date, dateLabel: a.dateLabel, count: 0, items: [] });
    }
    const bucket = byDay.get(key);
    bucket.count++;
    bucket.items.push(a);
  }

  // Only keep clusters with 2+ items
  const clusters = [...byDay.values()].filter(b => b.count >= 2);

  // Sort by date ascending (soonest first), tie-break by count descending
  clusters.sort((a, b) => {
    const dd = a.date - b.date;
    if (dd !== 0) return dd;
    return b.count - a.count;
  });

  return clusters;
}

function diffDays(from, to) {
  return Math.max(0, Math.ceil((to - from) / (24 * 60 * 60 * 1000)));
}
